import * as React from 'react';
import { SelectReact } from 'common/components/select';
import { SelectTheme } from 'common/components/select/constants';
import { useTranslation } from 'common/i18n/hooks';
import { useDataGroups } from 'common/contexts/react/data-group-context';
import { ButtonSize } from 'common/components/button/constants';
import { DEFAULT_DATA_GROUP } from 'common/widgets/data-group-selector';
import { ChangeEvent } from 'common/components/select/types';
import { Directions } from 'common/components/drop-menu/types';
import { LabelTheme, SectionItem } from 'common/components/items-menu/types';
import { useCurrentProject } from 'common/contexts/react/current-project-context';
type DataGroupSelectProps = {
  selectedDataGroup?: string | null;
  onSelect: (dataGroupId: string) => void;
  readonly?: boolean;
  disabledReason?: string;
};

/** A wrapper around SelectReact for selecting a data group inline for a metric in the query
 * builder.
 *
 * Returns null if dataGroupsStore isn't populated or there are none. */
export function DataGroupSelect(props: DataGroupSelectProps) {
  var _useCurrentProject$ge, _dataGroups$find;
  const dataGroupStore = useDataGroups();
  const {
    t
  } = useTranslation();
  const currentProject = (_useCurrentProject$ge = useCurrentProject().get()) === null || _useCurrentProject$ge === void 0 ? void 0 : _useCurrentProject$ge.value;
  const upsellItem = React.useMemo(() => ({
    label: t("data-groups.add-groups"),
    tag: t("common.upgrade"),
    value: "upsell",
    labelTheme: LabelTheme.Soft
  }), []);
  const addGroupItem = React.useMemo(() => ({
    label: t("data-groups.add-group"),
    value: "add-group",
    icon: "plus"
  }), []);
  const dataGroupsLoading = !dataGroupStore.value;
  const dataGroupsEmpty = dataGroupStore.value && Object.values(dataGroupStore.value).length === 0;
  const showUpsell = currentProject && !currentProject.canUseDataGroups();
  const showAddGroup = !dataGroupsLoading && !showUpsell && dataGroupsEmpty;
  const selectedId = props.selectedDataGroup || null;
  const dataGroups = [DEFAULT_DATA_GROUP, ...Object.values(dataGroupStore.value || {})];
  const items: SectionItem[] = dataGroups.map(dataGroup => {
    const label = dataGroup.groupID === DEFAULT_DATA_GROUP.groupID ? t("query-builder.dg-user") : dataGroup.displayName;
    return {
      isDisabled: !dataGroup.isReady,
      isSelected: selectedId === dataGroup.groupID,
      label,
      tooltip: dataGroup.displayName.length > 12 ? {
        text: dataGroup.displayName
      } : undefined,
      value: dataGroup.groupID
    };
  });
  if ((dataGroupsLoading || dataGroupsEmpty) && selectedId) {
    // if the selected data group is missing just show the selected ID
    items.push({
      label: selectedId,
      value: selectedId,
      isSelected: true
    });
  }
  if (showUpsell) {
    items.push(upsellItem);
  } else if (showAddGroup) {
    items.push(addGroupItem);
  }
  const userGroupSelected = selectedId === DEFAULT_DATA_GROUP.groupID;
  const selectedLabel = userGroupSelected ? t("query-builder.dg-user") : ((_dataGroups$find = dataGroups.find(dataGroup => dataGroup.groupID === selectedId)) === null || _dataGroups$find === void 0 ? void 0 : _dataGroups$find.displayName) || selectedId || undefined;
  const tooltipText = userGroupSelected ? t("data-groups.analyze-by-users-tooltip") : t("data-groups.analyze-by-dg-tooltip");
  const tooltip = {
    text: props.disabledReason || "".concat(tooltipText, "\n[").concat(t("common.learn-more"), "](https://help.mixpanel.com/hc/en-us/articles/360025333632)"),
    'delay-in': 200
  };
  return <SelectReact alwaysShowCaret className='data-group-select' label={selectedLabel} tooltip={tooltip} theme={SelectTheme.Tertiary} size={ButtonSize.Small} menuAlignment={Directions.DownRight} menuWidth={-1} elref='dataGroupSelect' sections={[{
    items
  }]} onChange={ev => {
    const value = (ev as ChangeEvent).detail.item.value;
    if (value === upsellItem.value) {
      const orgId = currentProject === null || currentProject === void 0 ? void 0 : currentProject.organizationId;
      window.open("/pricing/plan-builder/".concat(orgId ? "?org=".concat(orgId) : ""), "_blank");
    } else if (value === addGroupItem.value) {
      window.open("https://docs.mixpanel.com/docs/data-structure/group-analytics#group-keys-in-project-settings", "_blank");
    } else {
      props.onSelect(value);
    }
  }} readonly={props.readonly} data-sentry-element="SelectReact" data-sentry-component="DataGroupSelect" data-sentry-source-file="index.tsx" />;
}